import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { pxToRem } from '../../../utils/pxToRem';
import { conventionContent } from '../content';
import { SectionInner } from '../components/SectionInner';
import { SpeakerCard } from '../components/SpeakerCard';
import { DiplomatCard } from '../components/DiplomatCard';
import { SpeakerCarousel } from '../components/SpeakerCarousel';
import { CornerFrame } from '../components/CornerFrame';

export function Speakers() {
  const { speakers } = conventionContent;

  return (
    <Box
      component='section'
      id={speakers.sectionId}
      aria-labelledby={speakers.headingId}
      sx={{
        backgroundColor: 'background.paper',
        paddingBlock: { mobile: pxToRem(48), laptop: pxToRem(120) },
      }}
    >
      <SectionInner
        direction={{ mobile: 'column', laptop: 'row' }}
        gap={{ mobile: pxToRem(48), laptop: pxToRem(93) }}
        sx={{ alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            position: 'relative',
            width: { mobile: pxToRem(261), laptop: pxToRem(288) },
            height: { mobile: pxToRem(168), laptop: pxToRem(254) },
            flexShrink: 0,
            order: { mobile: 0, laptop: 2 },
          }}
        >
          <CornerFrame
            corner='bottom-left'
            inset={{
              mobile: { horizontal: 14.5, vertical: 15 },
              laptop: { horizontal: 19, vertical: 18.787 },
            }}
            radius={{ mobile: 20.295, laptop: 27 }}
          />
          <Typography
            component='h2'
            id={speakers.headingId}
            variant='h2'
            sx={{
              position: 'absolute',
              top: { mobile: pxToRem(6), laptop: pxToRem(14) },
              left: { mobile: '50%', laptop: pxToRem(32) },
              transform: { mobile: 'translateX(-50%)', laptop: 'none' },
              width: { mobile: pxToRem(239), laptop: pxToRem(257) },
              fontSize: { mobile: pxToRem(24), laptop: pxToRem(32) },
              lineHeight: { mobile: pxToRem(32), laptop: pxToRem(40) },
              letterSpacing: { mobile: pxToRem(-0.24), laptop: pxToRem(-0.64) },
              textAlign: { mobile: 'center', laptop: 'left' },
              margin: 0,
            }}
          >
            {/* Desktop: Figma 58:1307 breaks the heading over 5 lines; mobile wraps naturally */}
            <Box component='span' sx={{ display: { mobile: 'inline', laptop: 'none' } }}>
              {speakers.heading}
            </Box>
            <Box component='span' sx={{ display: { mobile: 'none', laptop: 'block' } }}>
              {speakers.headingLinesDesktop.map((line, i) => (
                <Box component='span' key={i} sx={{ display: 'block' }}>
                  {line}
                </Box>
              ))}
            </Box>
          </Typography>
        </Box>
        <Box
          sx={{
            width: { mobile: '100%', laptop: pxToRem(736) },
            flexShrink: 0,
            order: { mobile: 1, laptop: 1 },
          }}
        >
          {/* Mobile: full-bleed infinite carousels — speakers, then diplomats */}
          <Stack
            gap={pxToRem(48)}
            sx={{
              display: { mobile: 'flex', laptop: 'none' },
              width: `calc(100% + ${pxToRem(32)})`,
              marginInline: pxToRem(-16),
            }}
          >
            <SpeakerCarousel
              items={speakers.list}
              ariaLabel={speakers.carouselAriaLabel}
              renderItem={(speaker) => (
                <SpeakerCard name={speaker.name} title={speaker.title} photoSrc={speaker.photoSrc} />
              )}
            />
            <SpeakerCarousel
              items={speakers.diplomats}
              ariaLabel={speakers.diplomatsCarouselAriaLabel}
              renderItem={(diplomat) => (
                <DiplomatCard
                  name={diplomat.name}
                  title={diplomat.title}
                  flagSrc={diplomat.flagSrc}
                  flagAlt={diplomat.flagAlt}
                />
              )}
            />
          </Stack>
          {/* Desktop: 4 x 2 speaker grid + diplomats row (160px cards, 32px column / 22px row gaps — Figma 382:197) */}
          <Stack gap={pxToRem(32)} sx={{ display: { mobile: 'none', laptop: 'flex' }, width: '100%' }}>
            <Stack direction='row' flexWrap='wrap' columnGap={pxToRem(32)} rowGap={pxToRem(22)}>
              {speakers.list.map((speaker) => (
                <SpeakerCard
                  key={speaker.name}
                  name={speaker.name}
                  title={speaker.title}
                  photoSrc={speaker.photoSrc}
                />
              ))}
            </Stack>
            <Stack direction='row' flexWrap='wrap' columnGap={pxToRem(32)} sx={{ alignItems: 'flex-start' }}>
              {speakers.diplomats.map((diplomat) => (
                <DiplomatCard
                  key={diplomat.name}
                  name={diplomat.name}
                  title={diplomat.title}
                  flagSrc={diplomat.flagSrc}
                  flagAlt={diplomat.flagAlt}
                />
              ))}
            </Stack>
          </Stack>
        </Box>
      </SectionInner>
    </Box>
  );
}
